import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
	Box,
	Paper,
	ScrollArea,
	Text,
	Textarea,
	TextInput,
	UnstyledButton,
} from '@mantine/core'

const TRIGGER_CHAR = '@'
const QUERY_PATTERN = /^[a-zA-Z0-9_{}-]*$/
const TRIGGER_BOUNDARY_PATTERN = /[\s([{'"`]/
const DEFAULT_DROPDOWN_WIDTH = 320
const ACTIVE_BG_COLOR = 'rgba(34, 139, 230, 0.16)'
const ACTIVE_BORDER_COLOR = '#228be6'
const ACTIVE_TEXT_COLOR = '#0b5ed7'
const ACTIVE_SUBTEXT_COLOR = '#1864ab'
const CARET_STYLE_PROPERTIES = [
	'box-sizing',
	'width',
	'height',
	'overflow-x',
	'overflow-y',
	'border-top-width',
	'border-right-width',
	'border-bottom-width',
	'border-left-width',
	'padding-top',
	'padding-right',
	'padding-bottom',
	'padding-left',
	'font-style',
	'font-variant',
	'font-weight',
	'font-stretch',
	'font-size',
	'font-size-adjust',
	'line-height',
	'font-family',
	'text-align',
	'text-transform',
	'text-indent',
	'text-decoration',
	'letter-spacing',
	'word-spacing',
	'tab-size',
	'white-space',
	'word-break',
	'overflow-wrap',
]

const normalizeValue = (value) => String(value || '')
const isBrowser = typeof window !== 'undefined'
const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const normalizeSearchText = (value) =>
	normalizeValue(value).toLowerCase().replace(/[{}]/g, '').trim()

const getTokenKey = (token) => normalizeValue(token).replace(/[{}]/g, '').trim()

const findTriggerMatch = (value, cursorPosition) => {
	if (!Number.isFinite(cursorPosition)) return null
	const safeValue = normalizeValue(value)
	const beforeCursor = safeValue.slice(0, cursorPosition)
	const triggerIndex = beforeCursor.lastIndexOf(TRIGGER_CHAR)
	if (triggerIndex < 0) return null

	const previousCharacter = beforeCursor[triggerIndex - 1]
	if (
		previousCharacter &&
		previousCharacter !== '\n' &&
		!TRIGGER_BOUNDARY_PATTERN.test(previousCharacter)
	) {
		return null
	}

	const query = beforeCursor.slice(triggerIndex + 1)
	if (!QUERY_PATTERN.test(query)) return null

	return {
		triggerIndex,
		cursorPosition,
		query,
	}
}

const buildSearchableText = (option) =>
	[
		option.token,
		option.key,
		option.label,
		option.description,
		...(Array.isArray(option.aliases) ? option.aliases : []),
	]
		.map(normalizeSearchText)
		.filter(Boolean)
		.join(' ')

const buildOptions = (variableOptions) =>
	(variableOptions || [])
		.filter((option) => option?.token)
		.map((option) => ({
			token: option.token,
			key: option.key || getTokenKey(option.token),
			label: option.label || getTokenKey(option.token),
			description: option.description || 'Dynamic value inserted at send time.',
			aliases: option.aliases || [],
			searchText: buildSearchableText(option),
		}))

const areMatchesEquivalent = (left, right) => {
	if (!left && !right) return true
	if (!left || !right) return false
	return (
		left.triggerIndex === right.triggerIndex &&
		normalizeValue(left.query) === normalizeValue(right.query)
	)
}

const getCaretCoordinates = (inputElement, cursorPosition) => {
	if (!isBrowser || !inputElement || !Number.isFinite(cursorPosition))
		return null
	const computed = window.getComputedStyle(inputElement)
	const mirror = document.createElement('div')
	mirror.setAttribute('aria-hidden', 'true')
	mirror.style.position = 'absolute'
	mirror.style.top = '-9999px'
	mirror.style.left = '0'
	mirror.style.visibility = 'hidden'
	mirror.style.pointerEvents = 'none'

	CARET_STYLE_PROPERTIES.forEach((property) => {
		mirror.style.setProperty(property, computed.getPropertyValue(property))
	})

	const isInput = inputElement.nodeName === 'INPUT'
	mirror.style.whiteSpace = isInput ? 'pre' : 'pre-wrap'
	mirror.style.wordWrap = 'break-word'

	const beforeCursor = normalizeValue(inputElement.value).slice(
		0,
		cursorPosition,
	)
	const afterCursor =
		normalizeValue(inputElement.value).slice(cursorPosition) || '.'
	mirror.textContent = beforeCursor

	const marker = document.createElement('span')
	marker.textContent = afterCursor
	mirror.appendChild(marker)
	document.body.appendChild(mirror)

	const lineHeight = Number.parseFloat(computed.lineHeight)
	const coordinates = {
		left: marker.offsetLeft - inputElement.scrollLeft,
		top: marker.offsetTop - inputElement.scrollTop,
		height: Number.isFinite(lineHeight)
			? lineHeight
			: marker.offsetHeight || 20,
	}

	document.body.removeChild(mirror)
	return coordinates
}

const VariableTriggerTextarea = ({
	value,
	onChange,
	variableOptions = [],
	inputType = 'textarea',
	dropdownWidth = DEFAULT_DROPDOWN_WIDTH,
	...inputProps
}) => {
	const wrapperRef = useRef(null)
	const inputRef = useRef(null)
	const triggerMatchRef = useRef(null)
	const [triggerMatch, setTriggerMatch] = useState(null)
	const [highlightedIndex, setHighlightedIndex] = useState(-1)
	const [menuPosition, setMenuPosition] = useState({
		left: 8,
		top: 0,
		maxWidth: dropdownWidth,
	})

	const options = useMemo(
		() => buildOptions(variableOptions),
		[variableOptions],
	)
	const query = normalizeSearchText(triggerMatch?.query || '')

	const filteredOptions = useMemo(() => {
		if (!triggerMatch) return []
		if (!query) return options
		return options.filter((option) => option.searchText.includes(query))
	}, [options, query, triggerMatch])

	const isOpen = !!triggerMatch && filteredOptions.length > 0

	const updateMenuPosition = useCallback(
		(sourceElement, cursorPosition) => {
			if (!sourceElement || !wrapperRef.current) return
			const caretCoordinates = getCaretCoordinates(
				sourceElement,
				cursorPosition,
			)
			if (!caretCoordinates) return

			const wrapperRect = wrapperRef.current.getBoundingClientRect()
			const inputRect = sourceElement.getBoundingClientRect()
			const availableWidth = Math.max(220, wrapperRect.width - 16)
			const maxWidth = Math.min(dropdownWidth, availableWidth)
			const left = clamp(
				inputRect.left -
					wrapperRect.left +
					caretCoordinates.left +
					(sourceElement.nodeName === 'INPUT' ? 0 : 2),
				8,
				Math.max(8, wrapperRect.width - maxWidth - 8),
			)
			const top =
				inputRect.top -
				wrapperRect.top +
				caretCoordinates.top +
				caretCoordinates.height +
				6

			setMenuPosition({ left, top, maxWidth })
		},
		[dropdownWidth],
	)

	const updateTriggerMatch = (nextValue, cursorPosition, sourceElement) => {
		const match = findTriggerMatch(nextValue, cursorPosition)
		const previousMatch = triggerMatchRef.current
		const didMatchContextChange = !areMatchesEquivalent(previousMatch, match)
		triggerMatchRef.current = match
		setTriggerMatch(match)
		if (didMatchContextChange) {
			setHighlightedIndex(-1)
		}
		if (match) {
			updateMenuPosition(sourceElement || inputRef.current, cursorPosition)
		}
	}

	const handleChange = (event) => {
		const nextValue = event.currentTarget.value
		const cursorPosition = event.currentTarget.selectionStart
		onChange(nextValue)
		updateTriggerMatch(nextValue, cursorPosition, event.currentTarget)
	}

	const handleCursorUpdate = (event) => {
		updateTriggerMatch(
			event.currentTarget.value,
			event.currentTarget.selectionStart,
			event.currentTarget,
		)
	}

	const closeMenu = () => {
		triggerMatchRef.current = null
		setTriggerMatch(null)
		setHighlightedIndex(-1)
	}

	const selectOption = (option) => {
		if (!option || !triggerMatch) return
		const safeValue = normalizeValue(value)
		const beforeTrigger = safeValue.slice(0, triggerMatch.triggerIndex)
		const afterCursor = safeValue.slice(triggerMatch.cursorPosition)
		const needsTrailingSpace =
			afterCursor.length === 0 || /^[\s)\]}.,!?]/.test(afterCursor) ? '' : ' '
		const nextValue = `${beforeTrigger}${option.token}${needsTrailingSpace}${afterCursor}`
		const nextCursorPosition = (
			beforeTrigger +
			option.token +
			needsTrailingSpace
		).length

		onChange(nextValue)
		closeMenu()

		requestAnimationFrame(() => {
			if (!inputRef.current) return
			inputRef.current.focus()
			inputRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition)
			updateMenuPosition(inputRef.current, nextCursorPosition)
		})
	}

	const handleKeyDown = (event) => {
		if (!isOpen) return
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setHighlightedIndex((prev) =>
				prev + 1 >= filteredOptions.length ? 0 : prev + 1,
			)
			return
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault()
			setHighlightedIndex((prev) =>
				prev <= 0 ? filteredOptions.length - 1 : prev - 1,
			)
			return
		}
		if (event.key === 'Enter' || event.key === 'Tab') {
			event.preventDefault()
			selectOption(
				filteredOptions[highlightedIndex >= 0 ? highlightedIndex : 0] ||
					filteredOptions[0],
			)
			return
		}
		if (event.key === 'Escape') {
			event.preventDefault()
			closeMenu()
		}
	}

	const handleScroll = (event) => {
		if (triggerMatch) {
			updateMenuPosition(
				event.currentTarget,
				event.currentTarget.selectionStart,
			)
		}
	}

	const isTextInput = inputType === 'text'
	const InputComponent = isTextInput ? TextInput : Textarea

	return (
		<Box ref={wrapperRef} style={{ position: 'relative' }}>
			<InputComponent
				{...inputProps}
				ref={inputRef}
				value={value}
				onChange={handleChange}
				onClick={handleCursorUpdate}
				onSelect={handleCursorUpdate}
				onKeyDown={handleKeyDown}
				onScroll={handleScroll}
				onBlur={closeMenu}
			/>

			{isOpen && (
				<Paper
					withBorder
					shadow='md'
					radius='md'
					p={6}
					style={{
						position: 'absolute',
						left: menuPosition.left,
						top: menuPosition.top,
						width: '100%',
						maxWidth: menuPosition.maxWidth,
						zIndex: 50,
					}}
				>
					<ScrollArea.Autosize mah={240}>
						{filteredOptions.map((option, index) => {
							const isActive = highlightedIndex === index
							return (
								<UnstyledButton
									key={`${option.token}-${option.key}`}
									onMouseDown={(event) => event.preventDefault()}
									onMouseEnter={() =>
										setHighlightedIndex((prev) => (prev === index ? prev : index))
									}
									onClick={() => selectOption(option)}
									style={{
										display: 'block',
										width: '100%',
										borderRadius: 8,
										padding: '8px 10px',
										border: isActive
											? `1px solid ${ACTIVE_BORDER_COLOR}`
											: '1px solid transparent',
										backgroundColor: isActive
											? ACTIVE_BG_COLOR
											: 'transparent',
										transition: 'background-color 120ms ease, border-color 120ms ease',
									}}
								>
									<Text
										fw={600}
										size='sm'
										style={{ color: isActive ? ACTIVE_TEXT_COLOR : undefined }}
									>
										{option.label}
									</Text>
									<Text
										ff='monospace'
										size='xs'
										style={{
											color: isActive
												? ACTIVE_SUBTEXT_COLOR
												: 'var(--mantine-color-dimmed)',
										}}
									>
										{option.token}
									</Text>
									<Text
										size='xs'
										style={{
											color: isActive
												? ACTIVE_SUBTEXT_COLOR
												: 'var(--mantine-color-dimmed)',
										}}
										mt={2}
									>
										{option.description}
									</Text>
								</UnstyledButton>
							)
						})}
					</ScrollArea.Autosize>
				</Paper>
			)}

		</Box>
	)
}

export default VariableTriggerTextarea
